import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaUserCheck,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaFileCsv,
  FaYoutube,
  FaFacebookSquare,
  FaRupeeSign,
  FaHandHoldingUsd,
  FaPiggyBank,
  FaEye,
  FaThumbsUp,
  FaCommentDots,
  FaShareAlt,
  FaPhoneAlt,
  FaWhatsapp,
  FaExternalLinkAlt,
  FaCopy,
  FaTh,
  FaList,
  FaInbox,
  FaTrophy,
  FaLink,
  FaUserSlash,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaRedoAlt,
  FaPowerOff,
  FaUserPlus,
  FaBoxOpen,
  FaTrash,
  FaBan,
  FaUnlock,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes clFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes clFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes clBar { from { width: 0; } }
.cu-in { animation: clFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.cu-fade { animation: clFade .2s ease both; }
.cu-bar { animation: clBar .9s cubic-bezier(.2,.7,.2,1) both; }
.cu-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.cu-press:hover { transform: translateY(-1px); }
.cu-press:active { transform: scale(.96); }
.cu-card { transition: transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease; }
.cu-card:hover { transform: translateY(-4px); box-shadow: 0 20px 36px -20px rgba(15,23,42,.45); }
.cu-switch { transition: background-color .2s ease; }
.cu-knob { transition: transform .25s cubic-bezier(.2,.8,.2,1); }
.cu-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .cu-in, .cu-fade, .cu-bar { animation: none; }
  .cu-press:hover, .cu-press:active, .cu-card:hover { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500";

/* ================= helpers ================= */

const num = (v) => Number(v || 0);
const money = (n) => `₹${num(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const nm = (s) => (s && String(s).trim() ? String(s).trim() : null);
const noRef = (c) => !c.user_id || c.user_id === "null" || c.user_id === "NULL";
const platformOf = (c) => {
  const t = `${c.cust_type || ""} ${c.pkg_type || ""} ${c.link_type || ""} ${c.link || ""}`.toLowerCase();
  if (t.includes("facebook") || t.includes("fb.")) return "facebook";
  if (t.includes("youtube") || t.includes("youtu")) return "youtube";
  return "other";
};
const ytId = (url) => {
  const m = String(url || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};
// The server credits the referrer: package price x commission %
const commissionOf = (c) => (num(c.pkg_price) * num(c.comission)) / 100;
const paidOf = (c) => num(c.paid_amount) || num(c.pkg_price);
const monetized = (c) => String(c.monitization) === "1";
const waLink = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  return d ? `https://wa.me/${d.length === 10 ? `91${d}` : d}` : null;
};

const METRICS = [
  { key: "watch", label: "Watch", icon: FaEye, have: "watch_time", target: "target_watch_time", color: "bg-sky-500" },
  { key: "likes", label: "Likes", icon: FaThumbsUp, have: "likes_count", target: "target_likes", color: "bg-rose-500" },
  { key: "comments", label: "Comments", icon: FaCommentDots, have: "comments_count", target: "target_comments", color: "bg-amber-500" },
  { key: "share", label: "Shares", icon: FaShareAlt, have: "share_count", target: "target_share", color: "bg-emerald-500" },
];

// Watch time from the app is in milliseconds, the package target is in hours
const WATCH_UNIT_PER_SECOND = 1000;
const haveOf = (c, m) => (m.key === "watch" ? num(c.watch_time) / WATCH_UNIT_PER_SECOND / 3600 : num(c[m.have]));
const fmtDur = (hours) => {
  const s = Math.round(hours * 3600);
  if (s < 60) return `${s}s`;
  const mi = Math.floor(s / 60);
  if (mi < 60) return `${mi}m`;
  return `${Math.floor(mi / 60)}h ${mi % 60}m`;
};
const haveText = (c, m) => (m.key === "watch" ? fmtDur(haveOf(c, m)) : haveOf(c, m));

const progressOf = (c) => {
  const parts = METRICS.filter((m) => num(c[m.target]) > 0).map((m) => Math.min(100, (haveOf(c, m) / num(c[m.target])) * 100));
  if (!parts.length) return null;
  return Math.round(parts.reduce((s, p) => s + p, 0) / parts.length);
};

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="cu-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Ring = ({ pct, size = 56 }) => {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const p = pct === null ? 0 : pct;
  const done = p >= 100;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="6" fill="none" className="stroke-slate-100 dark:stroke-slate-700" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          className={done ? "stroke-emerald-500" : "stroke-teal-500"}
          style={{ strokeDasharray: c, strokeDashoffset: c - (c * p) / 100, transition: "stroke-dashoffset 1s cubic-bezier(.2,.7,.2,1)" }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold tabular-nums text-slate-800 dark:text-slate-100">
        {pct === null ? "–" : done ? <FaCheckCircle className="text-emerald-500" size={16} /> : `${p}%`}
      </span>
    </div>
  );
};

/* ================= page ================= */

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [view, setView] = useState("cards");
  const [query, setQuery] = useState("");
  const [platform, setPlatform] = useState("all");
  const [pkgF, setPkgF] = useState("all");
  const [refF, setRefF] = useState("all"); // all | self | referred
  const [monF, setMonF] = useState("all"); // all | on | off
  const [delivery, setDelivery] = useState("all"); // all | done | running | none
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(null);
  const [checked, setChecked] = useState([]);
  const [confirm, setConfirm] = useState(null); // {type, list}
  const [deleteText, setDeleteText] = useState("");
  const [working, setWorking] = useState(false);
  const [userStatus, setUserStatus] = useState({});
  const [usersLoaded, setUsersLoaded] = useState(false);
  const PAGE = 18;

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/customer.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const postUsers = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/users.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const [list, prof, users] = await Promise.all([post("customerList"), post("customerProfile").catch(() => null), postUsers("all-users").catch(() => null)]);
      if (Array.isArray(users)) {
        const sm = {};
        users.forEach((u) => (sm[String(u.user_id)] = u.status || "Live"));
        setUserStatus(sm);
        setUsersLoaded(true);
      }
      const base = Array.isArray(list?.customers) ? list.customers : [];
      const byId = {};
      (prof?.customers || []).forEach((p) => (byId[String(p.cust_id)] = p));
      setProfilesLoaded(!!prof?.customers);
      // merge delivery targets + monetization into the list rows
      setCustomers(base.map((c) => ({ ...(byId[String(c.cust_id)] || {}), ...c, monitization: byId[String(c.cust_id)]?.monitization ?? c.monitization })));
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch customers" });
    }
    setLoading(false);
  }, [post, postUsers]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const toggleMon = async (c) => {
    const next = monetized(c) ? 0 : 1;
    setBusy(c.cust_id);
    try {
      const res = await post("toggleMonitization", { cust_id: c.cust_id, monitization: next });
      if (res?.error === 0) {
        setCustomers((list) => list.map((x) => (x.cust_id === c.cust_id ? { ...x, monitization: String(next) } : x)));
        toast.current?.show({ severity: "success", summary: next ? "Monetization on" : "Monetization off", detail: `${c.name}'s link is ${next ? "now shown to" : "hidden from"} group members`, life: 2500 });
      } else throw new Error(res?.message || "Failed");
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: e.message });
    }
    setBusy(null);
  };

  // Referrer account was deleted
  const refGone = (c) => !noRef(c) && (!nm(c.user_name) || (usersLoaded && !(String(c.user_id) in userStatus)));
  const refBlocked = (c) => !noRef(c) && String(userStatus[String(c.user_id)] || "").toLowerCase() === "block";

  const openConfirm = (type, list) => {
    setDeleteText("");
    setConfirm({ type, list });
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, list } = confirm;
    setWorking(true);
    let ok = 0;
    let needsServer = false;
    // block/unblock act on referrer accounts, once per referrer
    const targets = type === "blockRef" || type === "unblockRef" ? Array.from(new Map(list.map((c) => [String(c.user_id), c])).values()) : list;
    for (const c of targets) {
      try {
        let res;
        if (type === "delete") res = await post("deleteCustomer", { cust_id: c.cust_id });
        else if (type === "deactivate") res = await post("toggleMonitization", { cust_id: c.cust_id, monitization: 0 });
        else if (type === "activate") res = await post("toggleMonitization", { cust_id: c.cust_id, monitization: 1 });
        else res = await postUsers("block-user", { user_id: c.user_id, status: type === "blockRef" ? "Block" : "Live" });
        if (res && (res.status === false || (res.error !== undefined && res.error !== 0))) {
          if (String(res.error || res.message || "").toLowerCase().includes("invalid action")) needsServer = true;
          throw new Error(res.message || res.error || "Failed");
        }
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setChecked([]);
    const words = { delete: "deleted", deactivate: "deactivated", activate: "activated", blockRef: "referrers blocked", unblockRef: "referrers unblocked" };
    if (ok) toast.current?.show({ severity: "success", summary: "Done", detail: `${ok} ${type.includes("Ref") ? "" : ok === 1 ? "customer " : "customers "}${words[type]}` });
    if (needsServer) {
      toast.current?.show({ severity: "warn", summary: "Server update needed", detail: "Delete customer is not on the server yet. Add the 'deleteCustomer' action to customer.php.", life: 8000 });
    } else if (ok < targets.length) {
      toast.current?.show({ severity: "error", summary: "Error", detail: `${targets.length - ok} could not be done` });
    }
    fetchCustomers();
  };

  /* ----- derived ----- */

  const q = query.trim();

  // same phone = same person buying again
  const orderInfo = useMemo(() => {
    const byPhone = {};
    [...customers]
      .sort((a, b) => (parseAny(a.date_time)?.getTime() || 0) - (parseAny(b.date_time)?.getTime() || 0))
      .forEach((c) => {
        const k = String(c.contact_num || c.cust_id);
        if (!byPhone[k]) byPhone[k] = [];
        byPhone[k].push(c.cust_id);
      });
    const m = {};
    Object.values(byPhone).forEach((ids) => ids.forEach((id, i) => (m[id] = { n: i + 1, of: ids.length })));
    return m;
  }, [customers]);

  const pkgs = useMemo(() => {
    const m = {};
    customers.forEach((c) => {
      if (!m[c.pkg_id]) m[c.pkg_id] = { id: c.pkg_id, name: c.pkg_name || `Package ${c.pkg_id}`, price: num(c.pkg_price), count: 0, revenue: 0 };
      m[c.pkg_id].count += 1;
      m[c.pkg_id].revenue += paidOf(c);
    });
    return Object.values(m).sort((a, b) => b.revenue - a.revenue);
  }, [customers]);

  const referrers = useMemo(() => {
    const m = {};
    customers.forEach((c) => {
      if (noRef(c)) return;
      const k = String(c.user_id);
      if (!m[k]) m[k] = { id: k, name: nm(c.user_name), count: 0, earned: 0 };
      m[k].count += 1;
      m[k].earned += commissionOf(c);
    });
    return Object.values(m).sort((a, b) => b.count - a.count || b.earned - a.earned);
  }, [customers]);

  const stats = useMemo(() => {
    const revenue = customers.reduce((s, c) => s + paidOf(c), 0);
    const comm = customers.reduce((s, c) => s + commissionOf(c), 0);
    return {
      count: customers.length,
      people: new Set(customers.map((c) => c.contact_num || c.cust_id)).size,
      revenue,
      comm,
      net: revenue - comm,
      monOn: customers.filter(monetized).length,
      done: customers.filter((c) => progressOf(c) === 100).length,
      repeat: Object.values(orderInfo).filter((o) => o.n > 1).length,
    };
  }, [customers, orderInfo]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    let l = customers.filter((c) => {
      if (platform !== "all" && platformOf(c) !== platform) return false;
      if (pkgF !== "all" && String(c.pkg_id) !== String(pkgF)) return false;
      if (refF === "self" && !noRef(c)) return false;
      if (refF === "referred" && noRef(c)) return false;
      if (refF === "deleted" && !refGone(c)) return false;
      if (refF === "blocked" && !refBlocked(c)) return false;
      if (monF === "on" && !monetized(c)) return false;
      if (monF === "off" && monetized(c)) return false;
      const pr = progressOf(c);
      if (delivery === "done" && pr !== 100) return false;
      if (delivery === "running" && (pr === null || pr === 100)) return false;
      if (delivery === "none" && pr !== null) return false;
      if (!ql) return true;
      return [c.cust_id, c.name, c.contact_num, refGone(c) ? "unknown user" : c.user_name, c.user_id, c.pkg_name, c.link].map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(ql));
    });
    l = [...l].sort((a, b) => {
      if (sortBy === "paid") return paidOf(b) - paidOf(a);
      if (sortBy === "progress") return (progressOf(a) ?? -1) - (progressOf(b) ?? -1);
      if (sortBy === "name") return String(a.name || "").localeCompare(String(b.name || ""));
      const da = parseAny(a.date_time)?.getTime() || 0;
      const db = parseAny(b.date_time)?.getTime() || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });
    return l;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, q, platform, pkgF, refF, monF, delivery, sortBy, userStatus, usersLoaded]);

  useEffect(() => setPage(1), [q, platform, pkgF, refF, monF, delivery, sortBy, view]);
  useEffect(() => {
    const valid = new Set(customers.map((c) => c.cust_id));
    setChecked((x) => x.filter((id) => valid.has(id)));
  }, [customers]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE, safePage * PAGE);

  const hasFilters = q || platform !== "all" || pkgF !== "all" || refF !== "all" || monF !== "all" || delivery !== "all";
  const clearFilters = () => {
    setQuery("");
    setPlatform("all");
    setPkgF("all");
    setRefF("all");
    setMonF("all");
    setDelivery("all");
  };

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") setConfirm((c) => (working ? c : null));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [working]);

  const copyText = (t, label = "Copied") => {
    navigator.clipboard?.writeText(String(t));
    toast.current?.show({ severity: "info", summary: label, detail: String(t), life: 1500 });
  };

  const exportCsv = () => {
    const head = ["Cust ID", "Name", "Contact", "Type", "Package", "Package Price", "Paid", "Joined By", "Referrer ID", "Commission %", "Commission", "Monetization", "Delivery %", "Link", "Date"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = filtered.map((c) =>
      [c.cust_id, c.name, c.contact_num, c.cust_type, c.pkg_name, c.pkg_price, paidOf(c), noRef(c) ? "Self" : nm(c.user_name) || "Unknown user", noRef(c) ? "" : c.user_id, c.comission, commissionOf(c).toFixed(2), monetized(c) ? "On" : "Off", progressOf(c) ?? "", c.link, c.date_time]
        .map(esc)
        .join(",")
    );
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ycnp-customers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ----- pieces ----- */

  const MonSwitch = ({ c, small }) => (
    <button
      type="button"
      role="switch"
      aria-checked={monetized(c)}
      onClick={(e) => {
        e.stopPropagation();
        toggleMon(c);
      }}
      disabled={busy === c.cust_id || !profilesLoaded}
      title={monetized(c) ? "Monetization is on. Click to turn off." : "Monetization is off. Click to turn on."}
      className={`cu-press inline-flex items-center gap-2 ${small ? "" : "h-8 pl-1 pr-3 rounded-full border"} ${monetized(c) ? (small ? "" : "border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20") : small ? "" : "border-slate-200 dark:border-slate-600"} disabled:opacity-50`}
    >
      <span className={`cu-switch relative w-10 h-6 rounded-full ${monetized(c) ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}>
        <span className={`cu-knob absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow flex items-center justify-center ${monetized(c) ? "translate-x-4" : ""}`}>
          {busy === c.cust_id ? <FaSyncAlt className="animate-spin text-slate-400" size={8} /> : <FaPowerOff size={8} className={monetized(c) ? "text-emerald-600" : "text-slate-400"} />}
        </span>
      </span>
      {!small && <span className={`text-xs font-semibold ${monetized(c) ? "text-emerald-700 dark:text-emerald-300" : "text-slate-500"}`}>{monetized(c) ? "Monetized" : "Not monetized"}</span>}
    </button>
  );

  const card = (c, i) => {
    const pl = platformOf(c);
    const pr = progressOf(c);
    const d = parseAny(c.date_time);
    const order = orderInfo[c.cust_id];
    const id = ytId(c.link);
    const ref = !noRef(c);
    return (
      <article key={c.cust_id} className={`cu-card cu-in rounded-2xl bg-white dark:bg-secondary-dark-bg border overflow-hidden flex flex-col ${checked.includes(c.cust_id) ? "border-teal-500 ring-2 ring-teal-500/30" : refGone(c) ? "border-rose-200 dark:border-rose-900" : "border-slate-200 dark:border-slate-700"}`} style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}>
        {/* top stripe */}
        <div className={`h-1.5 ${pl === "facebook" ? "bg-blue-600" : pl === "youtube" ? "bg-red-600" : "bg-teal-500"}`} />
        <div className="p-4 flex items-start gap-3">
          <input type="checkbox" checked={checked.includes(c.cust_id)} onChange={() => setChecked((x) => (x.includes(c.cust_id) ? x.filter((y) => y !== c.cust_id) : [...x, c.cust_id]))} className="mt-4 w-4 h-4 accent-teal-600 cursor-pointer" aria-label="Select customer" />
          <Ring pct={pr} />
          <div className="flex-1 min-w-0">
            <button type="button" onClick={() => window.open(`https://customer.ycnp.in/customer/${c.cust_id}`, "_blank")} className="block text-left font-semibold text-slate-900 dark:text-white truncate hover:text-teal-700 hover:underline" title="Open customer page">
              <Hi text={c.name || "No name"} q={q} />
            </button>
            <p className="text-xs text-slate-500 tabular-nums flex items-center gap-2">
              <span>
                <FaPhoneAlt size={9} className="inline mr-1" />
                <Hi text={c.contact_num || "-"} q={q} />
              </span>
              <button type="button" onClick={() => copyText(c.cust_id, "Customer ID copied")} className="hover:text-slate-800 inline-flex items-center gap-1">
                #<Hi text={c.cust_id} q={q} /> <FaCopy size={8} />
              </button>
            </p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${pl === "facebook" ? "bg-blue-100 text-blue-700" : pl === "youtube" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                {pl === "facebook" ? <FaFacebookSquare size={9} /> : pl === "youtube" ? <FaYoutube size={9} /> : null} {c.cust_type || "Customer"}
              </span>
              {order && order.of > 1 && (
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${order.n > 1 ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"}`} title="Same phone number bought more than once">
                  <FaRedoAlt size={8} /> Order {order.n} of {order.of}
                </span>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xl font-black tabular-nums text-slate-900 dark:text-white">{money(paidOf(c))}</p>
            <p className="text-[11px] font-semibold text-teal-700 dark:text-teal-300 truncate max-w-[120px]">
              <Hi text={c.pkg_name || `Package ${c.pkg_id}`} q={q} />
            </p>
          </div>
        </div>

        {/* delivery */}
        <div className="px-4">
          <div className="grid grid-cols-4 gap-2">
            {METRICS.map((m) => {
              const target = num(c[m.target]);
              const have = haveOf(c, m);
              const pct = target ? Math.min(100, (have / target) * 100) : 0;
              const Icon = m.icon;
              return (
                <div key={m.key} className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2" title={target ? `${haveText(c, m)} of ${target}${m.key === "watch" ? "h" : ""}` : "No target in this package"}>
                  <p className="flex items-center gap-1 text-[10px] text-slate-500">
                    <Icon size={9} /> {m.label}
                  </p>
                  <p className="text-xs font-bold tabular-nums text-slate-900 dark:text-white">{target ? `${haveText(c, m)}/${target}${m.key === "watch" ? "h" : ""}` : "–"}</p>
                  <div className="mt-1 h-1 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div className={`cu-bar h-full ${pct >= 100 ? "bg-emerald-500" : m.color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* link */}
        <div className="px-4 mt-3">
          {c.link ? (
            <a href={c.link} target="_blank" rel="noreferrer" className="group flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 p-1.5 hover:border-teal-400">
              {id ? <img src={`https://img.youtube.com/vi/${id}/default.jpg`} alt="" className="w-14 h-9 rounded object-cover" /> : <span className="w-14 h-9 rounded bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-400"><FaLink size={11} /></span>}
              <span className="flex-1 min-w-0 truncate text-xs text-slate-600 dark:text-slate-300 group-hover:text-teal-700">
                <Hi text={c.link} q={q} />
              </span>
              <FaExternalLinkAlt size={9} className="text-slate-400 shrink-0 mr-1" />
            </a>
          ) : (
            <p className="text-xs text-slate-400 italic rounded-lg border border-dashed border-slate-200 dark:border-slate-700 p-2">No link added yet</p>
          )}
        </div>

        {/* money split */}
        <div className="px-4 mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2">
            <p className="text-[10px] text-slate-500">Paid</p>
            <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">{money(paidOf(c))}</p>
          </div>
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-2">
            <p className="text-[10px] text-amber-700">Commission ({num(c.comission)}%)</p>
            <p className="text-sm font-bold tabular-nums text-amber-700">{money(commissionOf(c))}</p>
          </div>
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-2">
            <p className="text-[10px] text-emerald-700">YCNP keeps</p>
            <p className="text-sm font-bold tabular-nums text-emerald-700">{money(paidOf(c) - commissionOf(c))}</p>
          </div>
        </div>

        {/* footer */}
        <div className="mt-auto pt-3">
          <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between gap-2">
            <div className="min-w-0 text-xs">
              <p className="text-slate-500">Joined by</p>
              {ref ? (
                <span className="flex items-center gap-1.5 min-w-0">
                  <button type="button" onClick={() => !refGone(c) && navigate(`/user-profile/${c.user_id}`)} className={`font-semibold truncate ${refGone(c) ? "text-slate-400 italic cursor-default" : "text-slate-900 dark:text-white hover:underline"}`}>
                    <Hi text={refGone(c) ? `Unknown user (${c.user_id})` : nm(c.user_name)} q={q} />
                  </button>
                  {refGone(c) && <span className="shrink-0 text-[9px] font-bold px-1 py-0.5 rounded bg-rose-100 text-rose-700">DELETED</span>}
                  {refBlocked(c) && <span className="shrink-0 text-[9px] font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-800">BLOCKED</span>}
                </span>
              ) : (
                <p className="font-semibold text-teal-700">Self (no referrer)</p>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {waLink(c.contact_num) && (
                <a href={waLink(c.contact_num)} target="_blank" rel="noreferrer" className="cu-press w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center" aria-label="WhatsApp" title="WhatsApp customer">
                  <FaWhatsapp size={13} />
                </a>
              )}
              <MonSwitch c={c} small />
            </div>
          </div>
          <p className="px-4 pb-3 -mt-1 text-[11px] text-slate-400">
            {fmtDate(d)}, {fmtTime(d)}
          </p>
          <div className="grid grid-cols-3 border-t border-slate-100 dark:border-slate-700 divide-x divide-slate-100 dark:divide-slate-700">
            <button
              type="button"
              onClick={() => openConfirm(monetized(c) ? "deactivate" : "activate", [c])}
              disabled={!profilesLoaded}
              className={`h-10 inline-flex items-center justify-center gap-1.5 text-xs font-semibold transition-colors disabled:opacity-40 ${monetized(c) ? "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40" : "text-emerald-700 hover:bg-emerald-50"}`}
            >
              <FaPowerOff size={10} /> {monetized(c) ? "Deactivate" : "Activate"}
            </button>
            <button
              type="button"
              onClick={() => openConfirm(refBlocked(c) ? "unblockRef" : "blockRef", [c])}
              disabled={noRef(c) || refGone(c)}
              title={noRef(c) ? "No referrer" : refGone(c) ? "Referrer account was deleted" : ""}
              className="h-10 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
            >
              {refBlocked(c) ? <FaUnlock size={10} /> : <FaBan size={10} />} {refBlocked(c) ? "Unblock ref." : "Block ref."}
            </button>
            <button type="button" onClick={() => openConfirm("delete", [c])} className="h-10 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors">
              <FaTrash size={10} /> Delete
            </button>
          </div>
        </div>
      </article>
    );
  };

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="cu-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Customer</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Customer List</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">People who bought a promotion package. Track delivery, money and who brought them.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => navigate("/customer/request")} className="cu-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
            <FaUserPlus size={12} /> Requests
          </button>
          <button type="button" onClick={() => navigate("/customer-packages")} className="cu-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
            <FaBoxOpen size={12} /> Packages
          </button>
          <button type="button" onClick={exportCsv} disabled={!filtered.length} className="cu-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-50">
            <FaFileCsv /> Export
          </button>
          <button type="button" onClick={fetchCustomers} disabled={loading} className="cu-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Money hero + leaderboard */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="cu-in relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-600 via-teal-700 to-slate-900 text-white p-6 xl:col-span-2">
          <div className="pointer-events-none absolute -right-12 -top-12 w-60 h-60 rounded-full bg-teal-300/20 blur-3xl" />
          <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Customers", value: stats.count, sub: `${stats.people} people, ${stats.repeat} repeat orders`, icon: FaUserCheck, fmt: (v) => v },
              { label: "Revenue", value: stats.revenue, sub: "total paid by customers", icon: FaRupeeSign, fmt: money },
              { label: "Commission paid", value: stats.comm, sub: "to referring users", icon: FaHandHoldingUsd, fmt: money },
              { label: "YCNP keeps", value: stats.net, sub: "revenue minus commission", icon: FaPiggyBank, fmt: money },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label}>
                  <p className="inline-flex items-center gap-1.5 text-xs text-teal-100/80">
                    <Icon size={11} /> {s.label}
                  </p>
                  <p className="text-2xl sm:text-3xl font-black tabular-nums">{loading && !customers.length ? "…" : s.fmt(s.value)}</p>
                  <p className="text-[11px] text-teal-100/70">{s.sub}</p>
                </div>
              );
            })}
          </div>
          <div className="relative mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button type="button" onClick={() => setMonF(monF === "on" ? "all" : "on")} className={`cu-press text-left rounded-2xl p-3 border ${monF === "on" ? "bg-white text-teal-900 border-white" : "bg-white/10 border-white/10"}`}>
              <p className="text-xs opacity-80">Monetization on</p>
              <p className="text-lg font-bold tabular-nums">
                {stats.monOn} <span className="text-xs font-normal opacity-70">of {stats.count}</span>
              </p>
              <div className="mt-1 h-1.5 rounded-full bg-black/10 overflow-hidden">
                <div className="cu-bar h-full bg-emerald-400" style={{ width: `${stats.count ? (stats.monOn / stats.count) * 100 : 0}%` }} />
              </div>
            </button>
            <button type="button" onClick={() => setDelivery(delivery === "done" ? "all" : "done")} className={`cu-press text-left rounded-2xl p-3 border ${delivery === "done" ? "bg-white text-teal-900 border-white" : "bg-white/10 border-white/10"}`}>
              <p className="text-xs opacity-80">Delivery complete</p>
              <p className="text-lg font-bold tabular-nums">
                {stats.done} <span className="text-xs font-normal opacity-70">of {stats.count}</span>
              </p>
              <div className="mt-1 h-1.5 rounded-full bg-black/10 overflow-hidden">
                <div className="cu-bar h-full bg-sky-300" style={{ width: `${stats.count ? (stats.done / stats.count) * 100 : 0}%` }} />
              </div>
            </button>
          </div>
          {pkgs.length > 0 && (
            <div className="relative mt-4 flex flex-wrap gap-2">
              {pkgs.map((p) => (
                <button key={p.id} type="button" onClick={() => setPkgF(String(pkgF) === String(p.id) ? "all" : p.id)} className={`cu-press inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-semibold border ${String(pkgF) === String(p.id) ? "bg-white text-teal-900 border-white" : "bg-white/5 border-white/15 text-white/90"}`}>
                  {p.name} <span className="opacity-70">{money(p.price)}</span> <span className="px-1.5 rounded bg-black/20 tabular-nums">{p.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`${CARD} cu-in p-5`} style={{ animationDelay: "80ms" }}>
          <div className="flex items-center gap-2">
            <FaTrophy className="text-amber-500" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Top referrers</h2>
          </div>
          <p className="text-xs text-slate-500">Users who brought the most customers</p>
          <ul className="mt-3 space-y-2">
            {referrers.slice(0, 6).map((r, i) => (
              <li key={r.id}>
                <button type="button" onClick={() => setQuery(r.name || r.id)} className="cu-press w-full flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/40 text-left">
                  <span className={`w-5 text-xs font-bold text-center ${i === 0 ? "text-amber-500" : "text-slate-400"}`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${r.name ? "text-slate-900 dark:text-white" : "text-slate-400 italic"}`}>{r.name || "Unknown user"}</p>
                    <p className="text-[11px] text-slate-500">
                      {r.count} customer{r.count > 1 ? "s" : ""} • earned {money(r.earned)}
                    </p>
                  </div>
                  {!r.name && <FaUserSlash className="text-rose-400" size={11} />}
                </button>
              </li>
            ))}
            {!referrers.length && <li className="text-sm text-slate-500">No referred customers yet</li>}
          </ul>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`${CARD} cu-in mt-4 p-4`} style={{ animationDelay: "160ms" }}>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, phone, customer ID, referrer, package or link" className={`${INPUT} w-full h-12 pl-11 pr-12 text-base`} />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {query ? (
                <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                  <FaTimes size={13} />
                </button>
              ) : (
                <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
              )}
            </div>
          </div>
          <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700 self-start">
            {[
              { id: "cards", icon: FaTh, label: "Cards" },
              { id: "table", icon: FaList, label: "Table" },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <button key={v.id} type="button" onClick={() => setView(v.id)} className={`inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${view === v.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                  <Icon size={12} /> {v.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
            {[
              { id: "all", label: "All" },
              { id: "youtube", label: "YouTube", icon: FaYoutube, cls: "text-red-600" },
              { id: "facebook", label: "Facebook", icon: FaFacebookSquare, cls: "text-blue-600" },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} type="button" onClick={() => setPlatform(t.id)} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium transition-all active:scale-95 ${platform === t.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                  {Icon && <Icon size={11} className={t.cls} />} {t.label}
                </button>
              );
            })}
          </div>
          <select value={pkgF} onChange={(e) => setPkgF(e.target.value)} className={`${INPUT} h-9 px-2`}>
            <option value="all">All packages</option>
            {pkgs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select value={refF} onChange={(e) => setRefF(e.target.value)} className={`${INPUT} h-9 px-2`}>
            <option value="all">Any referrer</option>
            <option value="referred">Brought by a user</option>
            <option value="self">Self (no referrer)</option>
            <option value="deleted">Referrer deleted (unknown)</option>
            <option value="blocked">Referrer blocked</option>
          </select>
          <select value={monF} onChange={(e) => setMonF(e.target.value)} className={`${INPUT} h-9 px-2`}>
            <option value="all">Monetization: any</option>
            <option value="on">Monetized</option>
            <option value="off">Not monetized</option>
          </select>
          <select value={delivery} onChange={(e) => setDelivery(e.target.value)} className={`${INPUT} h-9 px-2`}>
            <option value="all">Delivery: any</option>
            <option value="done">Completed</option>
            <option value="running">In progress</option>
            <option value="none">No target</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-9 px-2`}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="paid">Highest paid</option>
            <option value="progress">Least delivered first</option>
            <option value="name">Name A–Z</option>
          </select>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-teal-700 dark:text-teal-400 hover:underline">
              Clear all
            </button>
          )}
          <span className="ml-auto text-sm text-slate-600 dark:text-slate-300">
            <b>{filtered.length}</b> customers • <b className="text-teal-700 dark:text-teal-400">{money(filtered.reduce((s, c) => s + paidOf(c), 0))}</b>
          </span>
        </div>
      </div>

      {/* Unknown referrer alert */}
      {customers.some(refGone) && refF !== "deleted" && (
        <button type="button" onClick={() => setRefF("deleted")} className="cu-in cu-press mt-4 w-full flex items-center gap-3 rounded-2xl border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-900/20 px-4 py-3 text-left text-sm text-rose-900 dark:text-rose-200">
          <FaExclamationTriangle className="shrink-0" />
          <span className="flex-1">
            <b>{customers.filter(refGone).length}</b> customers were brought by users who no longer exist. Tap to see them, then select and delete or deactivate.
          </span>
        </button>
      )}

      {/* Bulk bar */}
      {checked.length > 0 && (
        <div className="cu-fade mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 px-4 py-2.5 text-sm">
          <span className="font-semibold text-teal-900 dark:text-teal-100 mr-2">{checked.length} selected</span>
          <button type="button" onClick={() => openConfirm("deactivate", customers.filter((c) => checked.includes(c.cust_id) && monetized(c)))} disabled={!customers.some((c) => checked.includes(c.cust_id) && monetized(c))} className="cu-press inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-slate-700 hover:bg-slate-800 text-white font-semibold disabled:opacity-40">
            <FaPowerOff size={10} /> Deactivate
          </button>
          <button type="button" onClick={() => openConfirm("activate", customers.filter((c) => checked.includes(c.cust_id) && !monetized(c)))} disabled={!customers.some((c) => checked.includes(c.cust_id) && !monetized(c))} className="cu-press inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-40">
            <FaPowerOff size={10} /> Activate
          </button>
          <button type="button" onClick={() => openConfirm("blockRef", customers.filter((c) => checked.includes(c.cust_id) && !noRef(c) && !refGone(c) && !refBlocked(c)))} disabled={!customers.some((c) => checked.includes(c.cust_id) && !noRef(c) && !refGone(c) && !refBlocked(c))} className="cu-press inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-40">
            <FaBan size={10} /> Block referrers
          </button>
          <button type="button" onClick={() => openConfirm("delete", customers.filter((c) => checked.includes(c.cust_id)))} className="cu-press inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold">
            <FaTrash size={10} /> Delete
          </button>
          <button type="button" onClick={() => setChecked(filtered.map((c) => c.cust_id))} className="text-teal-800 dark:text-teal-200 hover:underline">
            Select all {filtered.length}
          </button>
          <button type="button" onClick={() => setChecked([])} className="ml-auto text-slate-600 dark:text-slate-300 hover:underline">
            Clear selection
          </button>
        </div>
      )}

      {/* Content */}
      <div className="mt-4">
        {loading && !customers.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`${CARD} h-80 animate-pulse`} />
            ))}
          </div>
        ) : !filtered.length ? (
          <div className={`${CARD} px-5 py-16 text-center`}>
            <FaInbox className="mx-auto text-slate-300" size={36} />
            <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">{customers.length ? "No customer matches these filters" : "No customers yet"}</p>
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="mt-2 text-sm font-medium text-teal-700 hover:underline">
                Clear all filters
              </button>
            )}
          </div>
        ) : view === "cards" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">{pageRows.map(card)}</div>
        ) : (
          <div className={`${CARD} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="pl-4 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={pageRows.length > 0 && pageRows.every((c) => checked.includes(c.cust_id))}
                        onChange={() => {
                          const ids = pageRows.map((c) => c.cust_id);
                          const all = ids.every((id) => checked.includes(id));
                          setChecked((x) => (all ? x.filter((id) => !ids.includes(id)) : Array.from(new Set([...x, ...ids]))));
                        }}
                        className="w-4 h-4 accent-teal-600 cursor-pointer"
                        aria-label="Select page"
                      />
                    </th>
                    <th className="px-3 py-3 font-medium text-left">Customer</th>
                    <th className="px-3 py-3 font-medium text-left">Package</th>
                    <th className="px-3 py-3 font-medium text-left">Delivery</th>
                    <th className="px-3 py-3 font-medium text-left">Joined By</th>
                    <th className="px-3 py-3 font-medium text-right">Commission</th>
                    <th className="px-3 py-3 font-medium text-left">Monetization</th>
                    <th className="px-3 py-3 font-medium text-left">Date / Time</th>
                    <th className="px-3 py-3 pr-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {pageRows.map((c) => {
                    const pr = progressOf(c);
                    const d = parseAny(c.date_time);
                    const order = orderInfo[c.cust_id];
                    return (
                      <tr key={c.cust_id} className={`transition-colors ${checked.includes(c.cust_id) ? "bg-teal-50/60 dark:bg-teal-900/10" : "hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}>
                        <td className="pl-4 py-3">
                          <input type="checkbox" checked={checked.includes(c.cust_id)} onChange={() => setChecked((x) => (x.includes(c.cust_id) ? x.filter((y) => y !== c.cust_id) : [...x, c.cust_id]))} className="w-4 h-4 accent-teal-600 cursor-pointer" aria-label="Select customer" />
                        </td>
                        <td className="px-3 py-3">
                          <button type="button" onClick={() => window.open(`https://customer.ycnp.in/customer/${c.cust_id}`, "_blank")} className="font-semibold text-teal-700 dark:text-teal-300 hover:underline text-left">
                            <Hi text={c.name} q={q} />
                          </button>
                          <p className="text-xs text-slate-500 tabular-nums">
                            <Hi text={c.contact_num} q={q} /> • #<Hi text={c.cust_id} q={q} />
                            {order && order.n > 1 && <span className="ml-1 text-violet-600 font-semibold">(order {order.n})</span>}
                          </p>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <p className="font-semibold text-slate-900 dark:text-white">
                            {money(paidOf(c))} <Hi text={c.pkg_name} q={q} />
                          </p>
                          <p className="text-xs text-slate-500">{c.cust_type || "-"}</p>
                        </td>
                        <td className="px-3 py-3 min-w-[120px]">
                          {pr === null ? (
                            <span className="text-xs text-slate-400">No target</span>
                          ) : (
                            <>
                              <p className={`text-xs font-bold tabular-nums ${pr === 100 ? "text-emerald-600" : "text-slate-700 dark:text-slate-200"}`}>{pr === 100 ? "Completed" : `${pr}%`}</p>
                              <div className="mt-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                                <div className={`cu-bar h-full ${pr === 100 ? "bg-emerald-500" : "bg-teal-500"}`} style={{ width: `${pr}%` }} />
                              </div>
                            </>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {noRef(c) ? (
                            <span className="font-semibold text-teal-700">Self</span>
                          ) : (
                            <>
                              <p className={`font-semibold ${!refGone(c) ? "text-slate-900 dark:text-white" : "text-slate-400 italic"}`}>
                                <Hi text={refGone(c) ? "Unknown user" : nm(c.user_name)} q={q} />
                                {refGone(c) && <span className="not-italic ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-rose-100 text-rose-700">DELETED</span>}
                                {refBlocked(c) && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-800">BLOCKED</span>}
                              </p>
                              <p className="text-xs text-slate-500 tabular-nums">({c.user_id})</p>
                            </>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <p className="font-semibold text-amber-700 tabular-nums">{money(commissionOf(c))}</p>
                          <p className="text-xs text-slate-500">({num(c.comission)}%)</p>
                        </td>
                        <td className="px-3 py-3">
                          <MonSwitch c={c} />
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                          <div className="text-xs text-slate-500">{fmtTime(d)}</div>
                        </td>
                        <td className="px-3 py-3 pr-4">
                          <div className="flex justify-end gap-1.5">
                            <button type="button" onClick={() => openConfirm(refBlocked(c) ? "unblockRef" : "blockRef", [c])} disabled={noRef(c) || refGone(c)} title={refBlocked(c) ? "Unblock referrer" : "Block referrer"} aria-label="Block referrer" className="cu-press w-8 h-8 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center justify-center disabled:opacity-30">
                              {refBlocked(c) ? <FaUnlock size={11} /> : <FaBan size={11} />}
                            </button>
                            <button type="button" onClick={() => openConfirm("delete", [c])} title="Delete customer" aria-label="Delete customer" className="cu-press w-8 h-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white flex items-center justify-center">
                              <FaTrash size={11} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filtered.length > PAGE && (
          <div className="flex items-center justify-center gap-3 mt-5 text-sm">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="cu-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
              <FaChevronLeft size={11} />
            </button>
            <span className="tabular-nums text-slate-600 dark:text-slate-300">
              Page <b>{safePage}</b> of {totalPages}
            </span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="cu-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Next page">
              <FaChevronRight size={11} />
            </button>
          </div>
        )}
      </div>

      {confirm && confirm.list.length > 0 && (() => {
        const list = confirm.list;
        const t = confirm.type;
        const isDel = t === "delete";
        const ready = !isDel || deleteText.trim().toUpperCase() === "DELETE";
        const copy = {
          delete: { title: list.length === 1 ? `Delete ${list[0].name}'s customer record?` : `Delete ${list.length} customers?`, body: "The customer record is removed for good. Their link stops showing in groups. Money already paid and commission already given are not changed.", btn: "Delete forever", color: "bg-rose-600 hover:bg-rose-700" },
          deactivate: { title: list.length === 1 ? `Deactivate ${list[0].name}?` : `Deactivate ${list.length} customers?`, body: "Monetization is turned off. Their link is hidden from group members until you activate it again.", btn: "Yes, deactivate", color: "bg-slate-700 hover:bg-slate-800" },
          activate: { title: list.length === 1 ? `Activate ${list[0].name}?` : `Activate ${list.length} customers?`, body: "Monetization is turned on. Their link is shown to group members again.", btn: "Yes, activate", color: "bg-emerald-600 hover:bg-emerald-700" },
          blockRef: { title: "Block the referring user?", body: "The referrer's app account is blocked and they get an 'Account Suspended' notification. The customer record stays.", btn: "Yes, block", color: "bg-amber-600 hover:bg-amber-700" },
          unblockRef: { title: "Unblock the referring user?", body: "The referrer can use the app again.", btn: "Yes, unblock", color: "bg-emerald-600 hover:bg-emerald-700" },
        }[t];
        const showRef = t === "blockRef" || t === "unblockRef";
        return (
          <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
            <div className="cu-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
            <div className="cu-in relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
              <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isDel ? "bg-rose-100 text-rose-600" : t === "blockRef" ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-600"}`}>
                {isDel ? <FaExclamationTriangle size={18} /> : t.includes("Ref") ? <FaBan size={16} /> : <FaPowerOff size={16} />}
              </span>
              <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{copy.title}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{copy.body}</p>
              <div className="mt-4 max-h-40 overflow-y-auto rounded-xl bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
                {(showRef ? Array.from(new Map(list.map((c) => [String(c.user_id), c])).values()) : list).slice(0, 30).map((c) => (
                  <div key={showRef ? c.user_id : c.cust_id} className="flex items-center justify-between gap-3 p-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{showRef ? nm(c.user_name) || c.user_id : c.name}</p>
                      <p className="text-xs text-slate-500 tabular-nums">{showRef ? `Referrer ${c.user_id}` : `#${c.cust_id} • ${c.contact_num || ""}`}</p>
                    </div>
                    {!showRef && <span className="text-sm font-bold tabular-nums">{money(paidOf(c))}</span>}
                  </div>
                ))}
                {list.length > 30 && <p className="p-2.5 text-xs text-slate-500">and {list.length - 30} more</p>}
              </div>
              {isDel && (
                <div className="mt-4">
                  <label htmlFor="cu-del" className="text-sm text-slate-700 dark:text-slate-200">
                    Type <b>DELETE</b> to confirm
                  </label>
                  <input id="cu-del" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} autoFocus placeholder="DELETE" className={`${INPUT} w-full h-10 px-3 mt-1.5`} />
                </div>
              )}
              <div className="flex gap-2 mt-5">
                <button type="button" onClick={() => setConfirm(null)} disabled={working} className="cu-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                  Cancel
                </button>
                <button type="button" onClick={runConfirm} disabled={working || !ready} className={`cu-press flex-1 h-11 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 ${copy.color}`}>
                  {working && <FaSyncAlt className="animate-spin" size={12} />}
                  {working ? "Please wait..." : copy.btn}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CustomerList;